import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
    UntilDestroy,
    untilDestroyed,
} from '@ngneat/until-destroy';
import {
    EMPTY,
} from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ValidPhoneNumberComponent } from '~/app/shared/modals/valid-phone-number/valid-phone-number.component';
import { AuthenticationService } from '~/app/core/services/api/authentication/authentication.service';
import { AuthenticationFacade } from '~/app/core/state/authentication/authentication.facade';
import { AlertComponent } from '~/app/shared/components/elements/alert/alert.component';
import { H2Component } from '~/app/shared/components/elements/h2/h2.component';
import { LabelComponent } from '~/app/shared/components/elements/label/label.component';
import { ModalRef } from '~/app/shared/services/modal/modal-ref';
import { ModalService } from '~/app/shared/services/modal/modal.service';
import { ResumeFeatureContext } from '~/app/shared/types/resume-feature-context.type';
import {
    ButtonWithIconComponent
} from "~/app/shared/components/elements/buttons/button-with-icon/button-with-icon.component";

export type ModalEndFreetrialInputArgs = {
    context: ResumeFeatureContext | undefined,
}

@UntilDestroy()
@Component({
    selector: 'eb-end-freetrial',
    templateUrl: './end-freetrial.component.html',
    styleUrls: ['./end-freetrial.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        H2Component,
        AlertComponent,
        LabelComponent,
        TranslocoPipe,
        ButtonWithIconComponent,
    ]
})
export class EndFreetrialComponent implements OnDestroy {
    private authenticationService = inject(AuthenticationService);
    private authenticationFacade = inject(AuthenticationFacade);
    private modal = inject(ModalService);
    private cd = inject(ChangeDetectorRef);
    ref = inject<ModalRef<Record<string, unknown>, ModalEndFreetrialInputArgs>>(ModalRef);

    public loading = false;

    public success = false;

    public error = false;

    public errorMsg = '';
    private timeoutRef: any;
    public modalRef: ModalRef | null = null;

    public ngOnDestroy(): void {
        if (this.timeoutRef) {
            clearTimeout(this.timeoutRef);
        }
        this.modalRef?.dispose();
    }

    public close() {
        this.ref.close({});
    }

    public contactMe() {
        const user = this.authenticationFacade.getUserSnapshot();

        if (!user?.phoneVerified) {
            this.modalRef = this.modal.open(
                ValidPhoneNumberComponent,
                {
                    sessionCount: 0,
                },
                {
                    padding: true,
                    isOutsideClickCloseModal: true,
                    closable: true,
                },
            );

            this.modalRef.afterClosed$
                .pipe(
                    untilDestroyed(this),
                )
                .subscribe((result) => {
                    if (result) this.contactMe();
                });
        } else {
            this.loading = true;

            this.authenticationService.recallMe({}).pipe(
                catchError((error: HttpErrorResponse) => {
                    this.loading = false;

                    this.errorMsg = error.error?.message ?? error.error?.detail;
                    this.error = true;
                    this.cd.detectChanges();
                    return EMPTY;
                }),
                untilDestroyed(this),
            ).subscribe(() => {
                this.loading = false;
                this.success = true;
                this.cd.detectChanges();
                this.timeoutRef = setTimeout(() => {
                    this.ref.close({});
                }, 5000);
            });
        }
    }
}
